/**
 * Contrato das manutenções da casa: corretivas e preventivas.
 *
 * Papel: aqui ficam `STATUS_CORRETIVA` e `STATUS_PREVENTIVA`, de onde nascem os
 * dois enums do Postgres, `LIMITE_DE_TEXTO_DA_MANUTENCAO` e os schemas que os
 * formulários e as Functions usam.
 *
 * Como usar: nenhum campo de texto é obrigatório, e todo texto passa por
 * `textoOpcional`: `trim()`, e o vazio e o ausente viram `null`. Só a casa e o
 * status são exigidos, porque nenhum dos dois é digitado — a casa vem da tela e
 * o status, de um `select` de lista fechada. Um campo de texto exigido aqui faria
 * a API recusar com 400 um registro que a tela deixa salvar, e a pessoa veria
 * "Não foi possível salvar." sem campo nenhum marcado.
 *
 * "#" (`numero`), `periodicidade` e o `responsavel` da preventiva são texto
 * livre: "3A" e "A cada 45 dias" são valores válidos, e a tela oferece a
 * periodicidade só como sugestão. A ordem da lista não passa por aqui: `posicao`
 * é atribuída pela API e não entra nem na entrada nem na saída.
 *
 * `proximaVisita` é digitada, e nada a compara com `ultimaVisita`: a próxima pode
 * ser anterior à última, e a periodicidade não calcula data nenhuma.
 */
import { z } from 'zod';

import { dataOpcionalSchema, idSchema, textoOpcional } from './comum';
import { dinheiroOpcionalSchema } from './formatos';

/** Em que ponto está uma ocorrência corretiva. */
export const STATUS_CORRETIVA = ['concluido', 'agendado', 'em_orcamento'] as const;

/** Em que ponto está uma rotina preventiva. */
export const STATUS_PREVENTIVA = ['em_dia', 'em_andamento'] as const;

/**
 * O maior texto aceito em qualquer campo de manutenção.
 *
 * É proteção da API contra corpo absurdo, e não regra da tela: o maior texto
 * registrado tem 283 caracteres e a origem não limita nada. Um limite perto do
 * que existe recusaria a observação longa colada de um e-mail, e a tela só
 * diria "Não foi possível salvar.".
 */
export const LIMITE_DE_TEXTO_DA_MANUTENCAO = 5000;

export const statusCorretivaSchema = z.enum(STATUS_CORRETIVA, {
  message: 'Situação desconhecida.',
});

export const statusPreventivaSchema = z.enum(STATUS_PREVENTIVA, {
  message: 'Situação desconhecida.',
});

export type StatusCorretiva = z.infer<typeof statusCorretivaSchema>;
export type StatusPreventiva = z.infer<typeof statusPreventivaSchema>;

/** Todo texto de manutenção: aparado, com vazio e ausente como `null`. */
const textoDaManutencao = textoOpcional(LIMITE_DE_TEXTO_DA_MANUTENCAO);

/** A residência da manutenção, como a listagem a traz aninhada. */
export const residenciaDaManutencaoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/**
 * O que o formulário envia para criar ou alterar uma corretiva.
 *
 * `area` e `responsavel` continuam como quem escreveu os escreveu: unificar
 * grafias parecidas exigiria decidir quais descrevem a mesma pessoa, e há
 * homônimos entre elas. `custo` chega como número, ou vazio quando não há valor
 * lançado; `0` é valor, e não ausência.
 */
export const novaCorretivaSchema = z.object({
  residenciaId: idSchema,
  area: textoDaManutencao,
  descricao: textoDaManutencao,
  status: statusCorretivaSchema,
  responsavel: textoDaManutencao,
  atualizacao: dataOpcionalSchema,
  custo: dinheiroOpcionalSchema,
  observacao: textoDaManutencao,
});

export type NovaCorretiva = z.infer<typeof novaCorretivaSchema>;

/** Uma corretiva como a API a devolve. */
export const corretivaSchema = z.object({
  id: idSchema,
  residencia: residenciaDaManutencaoSchema,
  area: z.string().nullable(),
  descricao: z.string().nullable(),
  status: statusCorretivaSchema,
  responsavel: z.string().nullable(),
  atualizacao: z.string().nullable(),
  custo: z.number().nullable(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Corretiva = z.infer<typeof corretivaSchema>;

/**
 * O que o formulário envia para criar ou alterar uma preventiva.
 *
 * `numero` é o "#" da tela, como foi digitado; ele não é identidade nem ordem,
 * e repetir um número não impede nada — a chave é o `id`. `empresa` e
 * `responsavel` são dois textos livres sem relação entre si.
 */
export const novaPreventivaSchema = z.object({
  residenciaId: idSchema,
  numero: textoDaManutencao,
  elemento: textoDaManutencao,
  atividade: textoDaManutencao,
  status: statusPreventivaSchema,
  periodicidade: textoDaManutencao,
  empresa: textoDaManutencao,
  responsavel: textoDaManutencao,
  ultimaVisita: dataOpcionalSchema,
  proximaVisita: dataOpcionalSchema,
  custo: dinheiroOpcionalSchema,
  observacao: textoDaManutencao,
});

export type NovaPreventiva = z.infer<typeof novaPreventivaSchema>;

/** Uma preventiva como a API a devolve. */
export const preventivaSchema = z.object({
  id: idSchema,
  residencia: residenciaDaManutencaoSchema,
  numero: z.string().nullable(),
  elemento: z.string().nullable(),
  atividade: z.string().nullable(),
  status: statusPreventivaSchema,
  periodicidade: z.string().nullable(),
  empresa: z.string().nullable(),
  responsavel: z.string().nullable(),
  ultimaVisita: z.string().nullable(),
  proximaVisita: z.string().nullable(),
  custo: z.number().nullable(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Preventiva = z.infer<typeof preventivaSchema>;
